import { Component, OnInit, Optional, Inject } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, tap } from "rxjs/operators";
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LanguageService, DialogService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { of as observableOf, Subscription } from 'rxjs';
import { CampaignsService } from '../campaigns.service';
import { MediaObserver } from '@angular/flex-layout';
import * as moment from 'moment';
import * as _ from 'lodash';

@Component({
  selector: 'esc-campaigns-filter',
  templateUrl: './campaigns-filter.component.html',
  styleUrls: ['./campaigns-filter.component.scss']
})
export class CampaignsFilterComponent implements OnInit {

  constructor(
    private fb: FormBuilder,
    public media: MediaObserver,
    public campSvc: CampaignsService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    public dialogRef: MatDialogRef<CampaignsFilterComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  public loading = {
    status: false,
    error: false
  };
  private getDataSub: Subscription;
  public defaultOption = {
    status: "All",
    selected: true
  };

  statuses = [];
  filteredStatus = [];

  multiSelect = {
    selectedStatus: [this.defaultOption],
  };

  public formGroup: FormGroup;
  networkErrMsg = this.languageService.getTranslation("MESSAGE_ERROR_NETWORK_CONNECTION", {});

  ngOnInit() {
    this.initForm();
    this.detectFormChanges();
    // console.log(this.data);
  }

  initForm() {
    this.formGroup = this.fb.group({
      distribution_start_date: [_.get(this.data, 'web_filter.distribution_start_date') || null],
      distribution_end_date: [_.get(this.data, 'web_filter.distribution_end_date') || null],
      status: [_.get(this.data, 'web_filter.status') == 0 ? this.defaultOption : _.get(this.data, 'web_filter.status')]
    })

    if(_.get(this.data, 'web_filter.status') != 0) {
      let tempArr = [];

      _.forEach(_.get(this.data, 'web_filter.status'), e => {
        let tempObj = {
          status: e,
          selected: true
        };

        tempArr.push(tempObj);
      })

      this.multiSelect.selectedStatus = _.cloneDeep(tempArr);
    }
  }

  displayFn(data?: any): string | undefined {
    return _.get(data, 'status');
  }

  detectFormChanges() {
    this.formGroup.get('distribution_start_date').valueChanges.subscribe(val => {
      if(val) {
        this.formGroup.get('distribution_end_date').setValidators(null);
        this.formGroup.get('distribution_end_date').setValidators([
          Validators.required 
        ]);
        this.formGroup.get('distribution_end_date').markAsTouched();
      }
    })

    this.formGroup.get('distribution_end_date').valueChanges.subscribe(val => {
      if(val) {
        this.formGroup.get('distribution_start_date').setValidators(null);
        this.formGroup.get('distribution_start_date').setValidators([
          Validators.required 
        ]);
        this.formGroup.get('distribution_start_date').markAsTouched();
      }
    })

    this.formGroup.get('status').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      if(value) {
        this.filteredStatus = this.filteredStatus.filter(e => {
          return _.toLower(e.status).includes(_.toLower(value));
        })
      } else {
        this.filteredStatus = _.cloneDeep(this.statuses);
      }
    });
  }

  getInitialData(name) {
    this.autoCmpltClosed(name);
    this.getData(name);
  }

  getData(name) {
    this.loading[name] = true;

    switch (name) {
      case "status": {
        this.getDataSub = this.campSvc.getStatus().pipe(
          tap(() => this.loading.status = true),
          finalize(() => {
            this.loading.status = false;
            this.getDataSub.unsubscribe();
            return observableOf(null);
          }),
          catchError((err) => {
            console.error(err);
            this.loading.status = false;
            return observableOf(null);
          }))
          .subscribe((res: any) => {
            if (environment.showLogs) {
              console.log(res);
            }
            if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
              this.loading.error = true;
              this.statuses = [];
              this.dialogService.openSnackBar(this.networkErrMsg)
              return;
            }
            if (_.has(res, 'value') && (res.value == "Success")) {
              this.statuses = this.statuses.concat(res.data);
              this.checkSelected('statuses', 'selectedStatus');
              this.filteredStatus = _.cloneDeep(this.statuses);
            }
            this.loading.status = false;
          });
          break;
      }
    }
  }

  checkSelected(srcArray, targetArray) {
    if(this.multiSelect[targetArray].some(opt => opt.status == 'All')) {
      let tempObj = _.cloneDeep(this.defaultOption);
      tempObj.selected = false;
      this.toggleSelection(tempObj, this[srcArray], targetArray);
    } 
    else if (this.multiSelect[targetArray].length) {
      this[srcArray].forEach(e => {
        this.multiSelect[targetArray].forEach(v => {
          if(e.status == v.status) {
            e.selected = v.selected;
          }
        })
        if(e.status == 'All') {
          e.selected = false;
        }
      })
    } else {
      this[srcArray].forEach(e => {
        e.selected = false;
      })
    }
  }

  optionClicked(event: Event, data, srcArray, targetArray) {
    event.stopPropagation();
    this.toggleSelection(data, srcArray, targetArray);
  }

  toggleSelection(data, srcArray, targetArray) {
    data.selected = !data.selected;
    
    //if 'all' is selected, choose only all
    if(data.selected == true && data.status == 'All') {
      srcArray.forEach(e => {
        e.selected = true;
      });
      this.multiSelect[targetArray] = [];
      this.multiSelect[targetArray].push(this.defaultOption);
      this.disableCheckbox(srcArray, 'status', data);
    } 

    //if 'all' is deselected, unselect everything
    else if(data.selected == false && data.status == 'All') {
      srcArray.forEach(e => {
        e.selected = false;
      });
      this.multiSelect[targetArray].splice(this.multiSelect[targetArray].indexOf(data), 1);
      this.enableCheckbox(srcArray);
    } 

    else if(this.multiSelect[targetArray].indexOf(data) == -1) {
      //if other option is selected, check if 'all' is already selected and clear selection
      if(this.multiSelect[targetArray].indexOf(this.defaultOption) > -1) {
        srcArray.forEach(e => {
          e.selected = false;
        });
        this.multiSelect[targetArray] = [];
      } 
      //if data exists in array, remove from list
      else if(this.multiSelect[targetArray].some(opt => opt.status == data.status)) {
        let index = _.findIndex(this.multiSelect[targetArray], function(item) { return item['status'] == data.status })
        this.multiSelect[targetArray].splice(index, 1);
      } 
      else {
        this.multiSelect[targetArray].push(data);
      }
    } 
    
    else {
      this.multiSelect[targetArray].splice(this.multiSelect[targetArray].indexOf(data), 1);
    }
  }

  enableCheckbox(optionArray) {
    optionArray.forEach(e => {
      e.disabled = false;
    })
    this.filteredStatus = _.cloneDeep(optionArray);
  }
 
  disableCheckbox(optionArray, formControlName, data) {
    if (optionArray[0].selected && (this.formGroup.get(formControlName).value && this.formGroup.get(formControlName).value.status == "All") || (_.get(data, 'status') == 'All')) {
      optionArray.forEach(e => {
        if(e.status != 'All') {
          e.disabled = true;
        }
      })
      this.filteredStatus = _.cloneDeep(optionArray);
    } else {
      this.enableCheckbox(optionArray);
    }
  }

  assignFormValues(targetArray, formControlName) {
    let tempArr = this.multiSelect[targetArray].map(e => {
      if(e.status == 'All') {
        return 0;
      }

      return e.status;
    });
    this.formGroup.get(formControlName).setValue(tempArr);
    this.returnValue();
  }

  autoCmpltClosed(name) {
    let options = (name != 'status') ? (name + "s") : (name + "es");
    this[options] = [];

    //set index zero as "All" option
    this[options][0] = this.defaultOption;
  }
  
  returnValue() {
    let retObj = {
      distribution_start_date: _.get(this.formGroup.get('distribution_start_date'), 'value') || "",
      distribution_end_date: _.get(this.formGroup.get('distribution_end_date'), 'value') || "",
      status: _.get(this.formGroup.get('status'), 'value').length > 0 ? _.get(this.formGroup.get('status'), 'value') : [0],
    }

    if(retObj.distribution_start_date != "") {
      retObj.distribution_start_date = moment(retObj.distribution_start_date).format('YYYY-MM-DD');
    }
    if(retObj.distribution_end_date != "") {
      retObj.distribution_end_date = moment(retObj.distribution_end_date).format('YYYY-MM-DD');
    }

    if(this.formGroup.valid) {
      this.dialogRef.close(retObj);
    }
  }

  reset() {
    this.formGroup.reset({ emitEvent: false });
    this.formGroup.get('distribution_end_date').setValidators(null);
    this.formGroup.get('distribution_start_date').setValidators(null);
    this.formGroup.get('status').setValue(this.defaultOption);
    this.multiSelect.selectedStatus = [];
    this.multiSelect.selectedStatus.push(this.defaultOption);
  }
  
}